import { create } from 'zustand';
import type { GuestAccountRecord, PlayerPoints, PlayerPremium, PlayerShipUpgrades, PlayerSciences, PremiumTier, TickCheckpointEvent } from '@kaeliux/shared';
import { getEffectivePremiumTier } from '@kaeliux/game-engine';
import { getPlayerState } from '../services/science.service';
import { getShipUpgradeState } from '../services/shipUpgrade.service';
import { usePlanetStore } from './planetStore';

/**
 * Single source of truth for player-wide (not per-planet) live state:
 * sciences, ship-upgrade levels, and their respective player-scoped queues
 * (each its own single global FIFO on the player profile — see
 * `docs/game-details/ship-upgrades.md`). `useAuthStore().profile` is a
 * snapshot taken at login/race-selection and never refreshed, so it must
 * NOT be read for anything a gameplay action can mutate mid-session — see
 * `science.service.ts`'s own doc comment on `getPlayerState`. Every page
 * that needs current sciences/ship-upgrades (ZonePage, SciencesPage,
 * ShipyardPage, StarportPage) reads from this store instead of maintaining
 * its own independent copy; mutations call `usePlayerStore.setState(...)`
 * directly with the server's response, mirroring how `usePlanetStore` is
 * updated after planet actions.
 */
interface PlayerStoreState {
    sciences: PlayerSciences | null;
    tickCheckpoints: TickCheckpointEvent[];
    shipUpgrades: PlayerShipUpgrades | null;
    shipUpgradeQueue: TickCheckpointEvent[];
    /**
     * Server-computed ranking points, read straight off
     * `SciencePlayerState.points` (see that type's doc comment) — never
     * recomputed client-side.
     */
    points: PlayerPoints | null;
    /**
     * Player premium state (Dark Matter balance, tier + its expiry, unlocked
     * planet images) read from `universes/{u}/players/{uid}.premium` (issue
     * #99) — NEVER from `useAuthStore().profile` (premium state must never
     * live on the client-writable `users/{uid}` doc, where a player could
     * self-grant it). Loaded by `loadPlayerState`. The player's tier lives
     * here as `premium.tier`; derive the effective tier with
     * {@link selectEffectiveTier} — queue-limit badges (`ZonePage`,
     * `StarportPage`, `SciencesPage`, `BattleReportPage`) read it from there.
     */
    premium: PlayerPremium | null;
    /** Guest-account history (issue #332) — see `useAccountStanding`. */
    guestAccount: GuestAccountRecord | null;
    /** Whether a colonize flight has ever been dispatched this round — the tutorial's colonize step counts it even once the flight turned back. */
    colonizeFlightSent: boolean;
    isLoading: boolean;
    error: string | null;
    loadPlayerState: (userId: string, planetId: string) => Promise<void>;
    loadShipUpgradeState: (userId: string, planetId: string) => Promise<void>;
}

/**
 * The premium tier that applies right now — the player's `premium.tier` if
 * unexpired, else `normal`. UI gates (queue-limit badges, the Premium Store)
 * read this so a time-limited tier takes effect immediately and lapses
 * cleanly. The server enforces the same via `getEffectivePremiumTier`.
 */
export function selectEffectiveTier(s: Pick<PlayerStoreState, 'premium'>): PremiumTier {
    return getEffectivePremiumTier(s.premium?.tier, new Date());
}

// Monotonic counters bumped on every call start. `loadPlayerState` and
// `loadShipUpgradeState` are fired independently from uncoordinated call
// sites — `useGameLoop`'s 60 s heartbeat and its per-domain due-scan, plus
// whatever a page action triggers — so responses can resolve out of order.
// Without this guard, a slower call that read stale (pre-completion) state
// can resolve after a faster call already wrote fresher state, silently
// reverting a completed research or ship-upgrade level back to its
// pre-completion value — mirrors the epoch guard in
// `planetStore.tickCurrentPlanet`.
let playerStateEpoch = 0;
let shipUpgradeStateEpoch = 0;

export const usePlayerStore = create<PlayerStoreState>((set) => ({
    sciences: null,
    tickCheckpoints: [],
    shipUpgrades: null,
    shipUpgradeQueue: [],
    points: null,
    premium: null,
    guestAccount: null,
    colonizeFlightSent: false,
    isLoading: false,
    error: null,

    loadPlayerState: async (userId: string, planetId: string) => {
        set({ isLoading: true, error: null });
        const startedAtEpoch = ++playerStateEpoch;
        try {
            const state = await getPlayerState(userId, planetId);
            if (playerStateEpoch !== startedAtEpoch) return;
            // This call ticks the planet to resolve due research, and a
            // completed science can change the planet itself (Physics
            // multiplies energy production). Adopt that snapshot or the UI
            // keeps rendering pre-research resources until the next heartbeat.
            if (state.planet) usePlanetStore.getState().applyPlanet(state.planet);
            set({
                sciences: state.sciences,
                tickCheckpoints: state.tickCheckpoints,
                points: state.points,
                premium: state.premium ?? null,
                guestAccount: state.guestAccount ?? null,
                colonizeFlightSent: state.colonizeFlightSent ?? false,
                isLoading: false,
            });
        } catch (error: unknown) {
            if (playerStateEpoch !== startedAtEpoch) return;
            set({ error: (error as Error)?.message, isLoading: false });
        }
    },

    loadShipUpgradeState: async (userId: string, planetId: string) => {
        set({ isLoading: true, error: null });
        const startedAtEpoch = ++shipUpgradeStateEpoch;
        try {
            const state = await getShipUpgradeState(userId, planetId);
            if (shipUpgradeStateEpoch !== startedAtEpoch) return;
            if (state.planet) usePlanetStore.getState().applyPlanet(state.planet);
            set({
                shipUpgrades: state.shipUpgrades,
                shipUpgradeQueue: state.tickCheckpoints,
                isLoading: false,
            });
        } catch (error: unknown) {
            if (shipUpgradeStateEpoch !== startedAtEpoch) return;
            set({ error: (error as Error)?.message, isLoading: false });
        }
    },
}));
